import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setQuestionAnswer, setQuestionAnswerFrench } from "../../../../redux/actions";
import styles from "./IncomeCheck.module.scss";
import BackAnchor from "../../../../shared/components/BackAnchor";
import { addToGoogleDataLayer } from "../../../../shared/const/utls";
import { householdTypeOptions } from "../../../../shared/const/data";

const IncomeCheckView = ({ t, i18n }) => {
  const dispatch = useDispatch();
  const [amount, setAmount] = useState("75,000");
  const { questionDataList } = useSelector((state) => state?.app);
  const datesData = useSelector((state) => state.datesReducer);
  let currentLanguageDates;
  if (datesData) {
    currentLanguageDates = datesData[i18n.language];
  }

  const navigate = useNavigate();
  const params = useParams();
  const housingType =
    questionDataList[
      questionDataList.findIndex((question) => question.questionNumber === 8)
    ].questionAnswer;

  const singleNoKids =
    housingType === householdTypeOptions[0]?.value ? true : false;

  const singleParent =
    housingType === householdTypeOptions[1]?.value ? true : false;

  useEffect(() => {
    if (singleNoKids) {
      setAmount("55,000");
      dispatch(setQuestionAnswer(16, "55,000"));
      dispatch(setQuestionAnswerFrench(16, "55,000"));
    }
  }, [singleNoKids, dispatch]);

  const disableGApush = useMemo(() => {
    return JSON.parse(params.disableGA || false);
  }, [params]);

  useEffect(() => {
    if (!disableGApush) {
      try {
        addToGoogleDataLayer("income-check", "HARP Application", i18n.language);
        window.scrollTo(0, 0);
      } catch (e) {
        console.log(e);
      }
    }
    if (i18n?.language === "en") {
      document.title =
        "Annual household net income - HARP - Government of Nova Scotia";
    } else {
      document.title =
        "Revenu annuel net du ménage – PAC – Gouvernement de la Nouvelle-Écosse";
    }
  }, [i18n.language, disableGApush]);

  const onNext = (incomeAnswer) => () => {
    let answer = incomeAnswer === 'Yes' ? `Less than $${amount}` : incomeAnswer === 'No' ? `More than $${amount}` : 'Complicated';
    let answerFrench = incomeAnswer === 'Yes' ? t("harp_net_income.btn_under") + amount : incomeAnswer === 'No' ? t("harp_net_income.btn_over") + amount : t("harp_net_income.btn_complicated");
    try {
      dispatch(setQuestionAnswer(9, answer));
      dispatch(setQuestionAnswerFrench(9, answerFrench));
      dispatch(setQuestionAnswer(16, amount));
      dispatch(setQuestionAnswerFrench(16, amount));
      if (incomeAnswer === "Yes") {
        navigate(`/${i18n.language}${t("routes.harp_qualify")}`);
      } else if (incomeAnswer === "No") {
        navigate(
          `/${i18n.language}${t(
            "routes.not_eligible"
          )}?question="harp-income-check"`
        );
      } else {
        navigate(`/${i18n.language}${t("routes.situation_change")}`);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleReduxCleanup = () => {
    dispatch(setQuestionAnswer(9, ''));
    dispatch(setQuestionAnswerFrench(9, ''));
  }

  return (
    <div>
      <div className="container">
        <main>
          <article>
            <div className="row" style={{ paddingTop: "1em" }}></div>
            <BackAnchor backPath={"routes.household_type"} onBackClick={handleReduxCleanup} />
            <div className="row">
              {(singleNoKids || singleParent) && (
                <>
                  <div className="col-sm-8 col-xs-11 col-md-8">
                    <h1>
                      {t("harp_net_income.single_header")}
                      {currentLanguageDates && (
                        <span>{currentLanguageDates?.taxFiled}{t("harp_net_income.net_income")}</span>
                      )}

                    </h1>
                  </div>
                  <div className="col-sm-8 col-xs-11">
                    <p>
                      {t("harp_net_income.single_sub_header").replace('#YEAR',
                        currentLanguageDates?.taxFiled
                      )}
                      {t("harp_net_income.cra")}                    </p>
                  </div>
                </>
              )}
              {(!singleNoKids && !singleParent) && (
                <>
                  <div className="col-xs-11 col-sm-8">
                    <h1>
                      {t("harp_net_income.combined_header")}
                      {currentLanguageDates && (
                        <span>{currentLanguageDates?.taxFiled}{t("harp_net_income.net_income")}</span>
                      )}
                    </h1>
                  </div>
                  <div className="col-xs-11 col-sm-8">
                    <p>
                      {t("harp_net_income.combined_sub_header")}
                      {currentLanguageDates && (
                        <span>{currentLanguageDates?.taxFiled}</span>
                      )}
                      <span>{t("harp_net_income.combined_sub_header2")}</span>
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Yes/No Answer */}
            <div className="row">
              <div className="col-xs-12 col-md-8 col-md-12">
                <div style={{ display: "flow" }}>
                  <button
                    className={`${styles["question_button"]} btn-lg btn-default`}
                    style={{
                      minWidth: 230,
                      marginLeft: 0,
                      marginRight: 40,
                      marginTop: 20,
                      marginBottom: 20
                    }}
                    onClick={onNext("Yes")}
                    name={t("harp_net_income.btn_under")}
                  >
                    {t("harp_net_income.btn_under")}
                    {amount}
                  </button>

                  <button
                    className={`${styles["question_button"]} btn-lg btn-default`}
                    style={{
                      minWidth: 230,
                      marginLeft: 0,
                      marginRight: 40,
                      marginTop: 20,
                      marginBottom: 20
                    }}
                    onClick={onNext("No")}
                    value={false}
                    name={t("harp_net_income.btn_over")}
                  >
                    {t("harp_net_income.btn_over")}
                    {amount}
                  </button>
                  <button
                    className={`${styles["question_button"]} btn-lg btn-default`}
                    style={{
                      minWidth: 230,
                      marginLeft: 0,
                      marginRight: 0,
                      marginTop: 20,
                      marginBottom: 20
                    }}
                    onClick={onNext("complicated")}
                    value={null}
                    name={t("harp_net_income.btn_complicated")}
                  >
                    {t("harp_net_income.btn_complicated")}
                  </button>
                </div>
              </div>
            </div>
          </article>
        </main>
      </div>
    </div>
  );
};

export default withTranslation("common")(IncomeCheckView);
